// A response can end an authenticated session at any point, in two ways that need different words
// on screen: US-112329's ACCOUNT_DEACTIVATED ("deactivation while a session is active"), and an
// expiry the refresh could not rescue — a spent, revoked or absent refresh token. client.ts (lib)
// can't import the auth feature's error-code constant across the boundary, so it emits this
// generic, feature-agnostic signal instead; <AppShell> (which owns the equivalent onIdle-style
// logout side effect) subscribes to it and picks the copy and tone.

export type ForceLogoutReason = {
  /**
   * `deactivated` — the account was switched off mid-session (US-112329).
   * `expired` — the session ended and could not be refreshed.
   *
   * The two differ in tone as well as wording: deactivation is an account state the grower must
   * act on, an expiry is ordinary and warning-toned.
   */
  kind: 'deactivated' | 'expired';
  /**
   * The server's own message, when it sent one — ADR-0008 says prefer `error.message` over
   * anything the client invents. Absent for a session that expired locally with no request to
   * quote, in which case the subscriber supplies house copy.
   */
  message?: string;
};

type Listener = (reason: ForceLogoutReason) => void;
const listeners = new Set<Listener>();

export function onForceLogout(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Defaults to `deactivated` so US-112329's original call site keeps its meaning unchanged. */
export function emitForceLogout(reason: ForceLogoutReason = { kind: 'deactivated' }): void {
  listeners.forEach((listener) => listener(reason));
}
